import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../../common/loader/loader";
import PageHeader from "../../../common/pageheader/pageheader";
import AgReportTable from "../../../common/table/ReportTable";
import DataTable from "../../../common/table/DataTable";
import { api } from "../../../../resources/api";
import SearchSelect from "../../../common/select/SearchSelect";

function ProcessResult(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [semesterList, setSemesterList] = useState([]);
    const [moduleListSelect, setModuleListSelect] = useState([]);
    const [gradeSettingSelect, setGradeSettingSelect] = useState([]);
    const [gradeList, setGradeList] = useState([]);
    const [selectedSemester, setSelectedSemester] = useState("");
    const [selectedModules, setSelectedModules] = useState([]);
    const [selectedGradeSetting, setSelectedGradeSetting] = useState("");
    const [results, setResults] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingCount, setProcessingCount] = useState(0);
    const [currentStudent, setCurrentStudent] = useState(null);
    const [showComplete, setShowComplete] = useState(false);
    const [processStartTime, setProcessStartTime] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);

    // Batch (multi-module) state
    const [batchResults, setBatchResults] = useState([]);
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [batchCurrentModule, setBatchCurrentModule] = useState(null);
    const [batchModuleDone, setBatchModuleDone] = useState(0);

    // Module picker modal state
    const [showModuleModal, setShowModuleModal] = useState(false);
    const [checkedModuleCodes, setCheckedModuleCodes] = useState(new Set());

    const columns = ["S/N", "Matric No", "Name", "CA", "Exam", "Total", "Grade", "Decision", "Status"];

    const isMultiMode = selectedModules.length > 1;
    const isSingleMode = selectedModules.length === 1;

    useEffect(() => {
        let timer;
        if (isProcessing && processStartTime) {
            timer = setInterval(() => {
                setElapsedTime(Math.floor((Date.now() - processStartTime) / 1000));
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [isProcessing, processStartTime]);

    const formatDuration = (seconds) => {
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const getRecord = async () => {
        const { success, data } = await api.get("staff/assessment/exam/process/result/data");
        if (success && data.message === 'success') {
            setSemesterList(data.semester_list.map(item => ({ label: item.SemesterName, value: item.SemesterCode })));
            setGradeSettingSelect(data.grade_type.map(item => ({ label: item.GradeType, value: item.GradeType })));
            setGradeList(data.grade_list);
        }
        setIsLoading(false);
    };

    const getModules = async (semester) => {
        setSelectedSemester(semester);
        setSelectedModules([]);
        setResults([]);
        setTableData([]);
        setShowComplete(false);
        setShowBatchModal(false);
        setBatchResults([]);
        const { success, data } = await api.get(`staff/academics/process-running-module/timetable-modules/list/${semester}`);
        if (success && data) {
            setModuleListSelect(data.map(item => ({ label: `${item.ModuleCode} - ${item.Modulename}`, value: item.ModuleCode, name: item.Modulename })));
        }
    };

    const getResults = async (moduleCode, moduleName) => {
        setShowComplete(false);
        if (!selectedSemester || !moduleCode) return;

        setIsProcessing(true);
        const { success, data } = await api.post("staff/assessment/moderation/students", {
            moduleCode,
            semester: selectedSemester
        });

        if (success && data.students) {
            setResults(data.students);
            updateTable(data.students);
        }
        setIsProcessing(false);
    };

    const handleModuleChange = (selected) => {
        const arr = selected || [];
        setSelectedModules(arr);
        setResults([]);
        setTableData([]);
        setShowComplete(false);
        setShowBatchModal(false);
        setBatchResults([]);
        if (arr.length === 1) getResults(arr[0].value, arr[0].name);
    };

    const updateTable = (studentResults) => {
        const rows = studentResults.map((item, index) => [
            index + 1,
            item.StudentID,
            item.StudentName,
            parseFloat(item.CAScore || 0).toFixed(1),
            parseFloat(item.ExamScore || 0).toFixed(1),
            parseFloat(item.Total || 0).toFixed(1),
            item.StudentGrade || '--',
            item.Decision || '--',
            item.StudentGrade && item.StudentGrade !== '--' ? '✓ Processed' : '⏳ Pending'
        ]);
        setTableData(rows);
    };

    const getGradeForScore = (score, gradeType) => {
        const applicableGrades = gradeList.filter(g => g.GradeType === gradeType);
        for (const grade of applicableGrades) {
            if (score >= grade.MinRange && score <= grade.MaxRange) {
                return { Grade: grade.Grade, Decision: grade.Decision };
            }
        }
        return { Grade: '--', Decision: '--' };
    };

    const onProcessResult = async () => {
        if (!selectedSemester || selectedModules.length === 0 || !selectedGradeSetting) {
            return toast.error("Please select all fields");
        }

        const toProcess = results.filter(r => !r.StudentGrade || r.StudentGrade === '--');

        if (toProcess.length === 0) {
            return toast.info("All results already processed");
        }

        setIsProcessing(true);
        setProcessingCount(0);
        setProcessStartTime(Date.now());
        setElapsedTime(0);

        let processed = 0;
        let errors = 0;

        for (const result of toProcess) {
            const roundedCA = Math.round(parseFloat(result.CAScore) || 0);
            const roundedExam = Math.round(parseFloat(result.ExamScore) || 0);
            const total = Math.ceil(roundedCA + roundedExam);
            const gradeInfo = getGradeForScore(total, selectedGradeSetting);

            setCurrentStudent({ id: result.StudentID, name: result.StudentName, total, grade: gradeInfo.Grade });

            try {
                const { success } = await api.patch("staff/assessment/exam/process/result", {
                    EntryID: result.EntryID,
                    Total: total,
                    StudentGrade: gradeInfo.Grade,
                    Decision: gradeInfo.Decision
                });

                if (success) {
                    processed++;
                    setProcessingCount(processed);
                    result.StudentGrade = gradeInfo.Grade;
                    result.Decision = gradeInfo.Decision;
                    updateTable([...results]);
                } else {
                    errors++;
                }
            } catch {
                errors++;
            }
        }

        setIsProcessing(false);
        setCurrentStudent(null);
        setShowComplete(true);

        if (processed > 0) toast.success(`Successfully processed ${processed} result(s)`);
        if (errors > 0) toast.warning(`${errors} result(s) failed to process`);
    };

    const onReprocessAll = async () => {
        if (!selectedSemester || selectedModules.length === 0 || !selectedGradeSetting) {
            return toast.error("Please select all fields");
        }
        if (results.length === 0) return toast.info("No results to reprocess");

        setIsProcessing(true);
        setProcessingCount(0);
        setProcessStartTime(Date.now());
        setElapsedTime(0);

        let processed = 0, errors = 0;

        for (const result of results) {
            const roundedCA = Math.round(parseFloat(result.CAScore) || 0);
            const roundedExam = Math.round(parseFloat(result.ExamScore) || 0);
            const total = Math.ceil(roundedCA + roundedExam);
            const gradeInfo = getGradeForScore(total, selectedGradeSetting);
            setCurrentStudent({ id: result.StudentID, name: result.StudentName, total, grade: gradeInfo.Grade });

            try {
                const { success } = await api.patch("staff/assessment/exam/process/result", {
                    EntryID: result.EntryID,
                    Total: total,
                    StudentGrade: gradeInfo.Grade,
                    Decision: gradeInfo.Decision
                });
                if (success) {
                    processed++;
                    setProcessingCount(processed);
                    result.StudentGrade = gradeInfo.Grade;
                    result.Decision = gradeInfo.Decision;
                    updateTable([...results]);
                } else { errors++; }
            } catch { errors++; }
        }

        setIsProcessing(false);
        setCurrentStudent(null);
        setShowComplete(true);
        if (processed > 0) toast.success(`Successfully reprocessed ${processed} result(s)`);
        if (errors > 0) toast.warning(`${errors} result(s) failed to reprocess`);
    };

    const onProcessMultipleModules = async () => {
        if (!selectedSemester || selectedModules.length < 2 || !selectedGradeSetting) {
            return toast.error("Please select semester, at least 2 modules, and grade setting");
        }

        setIsProcessing(true);
        setProcessingCount(0);
        setProcessStartTime(Date.now());
        setElapsedTime(0);
        setBatchModuleDone(0);
        setBatchResults([]);

        const allBatchResults = [];

        for (let i = 0; i < selectedModules.length; i++) {
            const mod = selectedModules[i];
            setBatchCurrentModule({ code: mod.value, name: mod.name || mod.label, index: i + 1, total: selectedModules.length });

            let processed = 0;
            let errors = 0;

            try {
                const { success, data } = await api.post("staff/assessment/moderation/students", {
                    moduleCode: mod.value,
                    semester: selectedSemester
                });

                if (success && data.students) {
                    const toProcess = data.students.filter(r => !r.StudentGrade || r.StudentGrade === '--');

                    for (const result of toProcess) {
                        const roundedCA = Math.round(parseFloat(result.CAScore) || 0);
                        const roundedExam = Math.round(parseFloat(result.ExamScore) || 0);
                        const total = Math.ceil(roundedCA + roundedExam);
                        const gradeInfo = getGradeForScore(total, selectedGradeSetting);

                        try {
                            const { success: patchSuccess } = await api.patch("staff/assessment/exam/process/result", {
                                EntryID: result.EntryID,
                                Total: total,
                                StudentGrade: gradeInfo.Grade,
                                Decision: gradeInfo.Decision
                            });
                            if (patchSuccess) processed++;
                            else errors++;
                        } catch {
                            errors++;
                        }
                    }
                }
            } catch {
                errors++;
            }

            allBatchResults.push({ moduleCode: mod.value, moduleName: mod.name || mod.label, processed, errors, status: errors === 0 ? 'success' : processed > 0 ? 'partial' : 'failed' });
            setBatchModuleDone(i + 1);
        }

        setBatchResults(allBatchResults);
        setIsProcessing(false);
        setBatchCurrentModule(null);
        setShowBatchModal(true);

        const totalProcessed = allBatchResults.reduce((sum, r) => sum + r.processed, 0);
        toast.success(`Batch complete: ${totalProcessed} results processed across ${selectedModules.length} modules`);
    };

    const openModuleModal = () => {
        setCheckedModuleCodes(new Set(selectedModules.map(m => m.value)));
        setShowModuleModal(true);
    };

    const toggleModule = (code) => {
        setCheckedModuleCodes(prev => {
            const next = new Set(prev);
            next.has(code) ? next.delete(code) : next.add(code);
            return next;
        });
    };

    const confirmModuleSelection = () => {
        const selected = moduleListSelect.filter(m => checkedModuleCodes.has(m.value));
        handleModuleChange(selected);
        setShowModuleModal(false);
    };

    const resetProcess = () => {
        if (isMultiMode) {
            setShowBatchModal(false);
            setBatchResults([]);
            setSelectedModules([]);
            setResults([]);
            setTableData([]);
        } else {
            setShowComplete(false);
            if (selectedModules.length === 1) getResults(selectedModules[0].value, selectedModules[0].name);
        }
    };

    useEffect(() => { getRecord(); }, []);

    const pendingCount = results.filter(r => !r.StudentGrade || r.StudentGrade === '--').length;
    const processedCount = results.length - pendingCount;
    const totalToProcess = (isProcessing && pendingCount === 0) ? results.length : pendingCount;
    const progressPercent = results.length > 0 && totalToProcess > 0
        ? Math.round((processingCount / totalToProcess) * 100)
        : (results.length > 0 && totalToProcess === 0 ? 100 : 0);

    const avgTimePerStudent = processingCount > 0 ? elapsedTime / processingCount : 0;
    const remainingStudents = totalToProcess - processingCount;
    const estimatedRemaining = Math.round(avgTimePerStudent * remainingStudents);

    const batchTotalProcessed = batchResults.reduce((s, r) => s + r.processed, 0);
    const batchTotalErrors = batchResults.reduce((s, r) => s + r.errors, 0);

    return isLoading ? <Loader /> : (
        <div className="d-flex flex-column flex-row-fluid">
            <PageHeader title={"Process Result"} items={["Assessment", "Exams & Records", "Process Result"]} />
            <div className="flex-column-fluid">
                <div className="card shadow-sm">
                    <div className="card-body py-5">

                        {/* Step Indicator */}
                        <div className="d-flex justify-content-center mb-5">
                            <div className="d-flex align-items-center">
                                <div className={`d-flex align-items-center justify-content-center rounded-circle ${selectedModules.length > 0 ? 'bg-success' : 'bg-primary'}`} style={{ width: '40px', height: '40px' }}>
                                    <span className="text-white fw-bold">{selectedModules.length > 0 ? <i className="fa fa-check"></i> : '1'}</span>
                                </div>
                                <span className={`ms-2 me-4 fw-semibold ${selectedModules.length > 0 ? 'text-success' : 'text-primary'}`}>Select Module(s)</span>

                                <div className={`bg-${selectedModules.length > 0 ? 'primary' : 'secondary'}`} style={{ width: '50px', height: '3px' }}></div>

                                <div className={`d-flex align-items-center justify-content-center rounded-circle ms-4 ${isProcessing ? 'bg-primary' : (showComplete || showBatchModal) ? 'bg-success' : 'bg-secondary'}`} style={{ width: '40px', height: '40px' }}>
                                    <span className="text-white fw-bold">{(showComplete || showBatchModal) ? <i className="fa fa-check"></i> : '2'}</span>
                                </div>
                                <span className={`ms-2 me-4 fw-semibold ${isProcessing ? 'text-primary' : (showComplete || showBatchModal) ? 'text-success' : 'text-secondary'}`}>Processing</span>

                                <div className={`bg-${(showComplete || showBatchModal) ? 'primary' : 'secondary'}`} style={{ width: '50px', height: '3px' }}></div>

                                <div className={`d-flex align-items-center justify-content-center rounded-circle ms-4 ${(showComplete || showBatchModal) ? 'bg-primary' : 'bg-secondary'}`} style={{ width: '40px', height: '40px' }}>
                                    <span className="text-white fw-bold">3</span>
                                </div>
                                <span className={`ms-2 fw-semibold ${(showComplete || showBatchModal) ? 'text-primary' : 'text-secondary'}`}>Complete</span>
                            </div>
                        </div>

                        {/* Selection Row */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <label className="form-label fw-semibold">Semester</label>
                                <SearchSelect options={semesterList} onChange={(e) => getModules(e.value)} placeholder="Select Semester" isDisabled={isProcessing} />
                            </div>
                            <div className="col-md-4">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <label className="form-label fw-semibold mb-0">Module(s)</label>
                                    {selectedSemester && !isProcessing && moduleListSelect.length > 0 && (
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 text-primary"
                                                onClick={() => handleModuleChange(
                                                    selectedModules.length === moduleListSelect.length ? [] : moduleListSelect
                                                )}
                                            >
                                                {selectedModules.length === moduleListSelect.length
                                                    ? 'Deselect All'
                                                    : `Select All (${moduleListSelect.length})`}
                                            </button>
                                            {selectedModules.length > 0 && selectedModules.length < moduleListSelect.length && (
                                                <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => handleModuleChange([])}>
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-light-primary w-100 text-start"
                                    onClick={openModuleModal}
                                    disabled={!selectedSemester || isProcessing}
                                    style={{ minHeight: '40px' }}
                                >
                                    <i className="fa fa-th-list me-2"></i>
                                    {selectedModules.length === 0
                                        ? 'Pick modules...'
                                        : `${selectedModules.length} module${selectedModules.length !== 1 ? 's' : ''} selected`}
                                </button>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Grade Setting</label>
                                <SearchSelect options={gradeSettingSelect} onChange={(e) => setSelectedGradeSetting(e.value)} placeholder="Select Grade Type" isDisabled={isProcessing} />
                            </div>
                            <div className="col-md-3 d-flex align-items-end gap-2">
                                {showComplete ? (
                                    <button className="btn btn-success w-100" onClick={resetProcess}>
                                        <i className="fa fa-refresh me-2"></i>Reset
                                    </button>
                                ) : isMultiMode ? (
                                    <button
                                        className="btn btn-primary w-100"
                                        onClick={onProcessMultipleModules}
                                        disabled={isProcessing || !selectedGradeSetting}
                                    >
                                        {isProcessing ? (
                                            <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                                        ) : (
                                            <><i className="fa fa-cogs me-2"></i>Process All ({selectedModules.length})</>
                                        )}
                                    </button>
                                ) : (
                                    <>
                                        <button className="btn btn-primary flex-fill" onClick={onProcessResult} disabled={isProcessing || pendingCount === 0 || !selectedGradeSetting}>
                                            {isProcessing ? (
                                                <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                                            ) : (
                                                <><i className="fa fa-cogs me-2"></i>Process ({pendingCount})</>
                                            )}
                                        </button>
                                        {results.length > 0 && (
                                            <button className="btn btn-warning flex-fill" onClick={onReprocessAll} disabled={isProcessing || !selectedGradeSetting}>
                                                <i className="fa fa-redo me-2"></i>Reprocess All ({results.length})
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Multi-module: selected modules list */}
                        {isMultiMode && !isProcessing && !showBatchModal && (
                            <div className="mb-4">
                                {!selectedGradeSetting && (
                                    <div className="alert alert-warning py-2 mb-2">
                                        <i className="fa fa-exclamation-triangle me-1"></i>
                                        Select a grade setting to enable batch processing
                                    </div>
                                )}
                                <DataTable
                                    data={{
                                        columns: [
                                            { label: 'S/N', field: 'sn' },
                                            { label: 'Module Code', field: 'code' },
                                            { label: 'Module Name', field: 'name' },
                                        ],
                                        rows: selectedModules.map((m, i) => ({
                                            sn: i + 1,
                                            code: m.value,
                                            name: m.name || m.label,
                                        }))
                                    }}
                                />
                            </div>
                        )}

                        {/* Multi-module: batch progress */}
                        {isMultiMode && isProcessing && batchCurrentModule && (
                            <div className="card bg-light border-primary mb-4">
                                <div className="card-body">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                        <strong>Processing module {batchCurrentModule.index} of {batchCurrentModule.total}</strong>
                                    </div>
                                    <h6 className="mb-1">
                                        <span className="badge bg-secondary me-2">{batchCurrentModule.code}</span>
                                        {batchCurrentModule.name}
                                    </h6>
                                    <div className="progress mt-3" style={{ height: '20px' }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                            role="progressbar"
                                            style={{ width: `${Math.round((batchModuleDone / selectedModules.length) * 100)}%` }}
                                        >
                                            {batchModuleDone} of {selectedModules.length} modules
                                        </div>
                                    </div>
                                    <p className="text-center text-danger small mt-2 mb-0">
                                        <i className="fa fa-exclamation-circle me-1"></i>
                                        Do not close or refresh this page until processing is complete
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Single-module: Processing Progress Card */}
                        {isSingleMode && isProcessing && currentStudent && (
                            <div className="card bg-light border-primary mb-4">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-md-6">
                                            <h6 className="mb-1 text-primary"><i className="fa fa-user-graduate me-2"></i>Currently Processing:</h6>
                                            <p className="mb-0 fw-bold">{currentStudent.id} - {currentStudent.name}</p>
                                            <small className="text-muted">Total: {currentStudent.total} → Grade: <span className="text-success fw-bold">{currentStudent.grade}</span></small>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="row text-center">
                                                <div className="col-4">
                                                    <h5 className="text-primary mb-0">{formatDuration(elapsedTime)}</h5>
                                                    <small className="text-muted">Elapsed</small>
                                                </div>
                                                <div className="col-4">
                                                    <h5 className="text-warning mb-0">{formatDuration(estimatedRemaining)}</h5>
                                                    <small className="text-muted">Est. Remaining</small>
                                                </div>
                                                <div className="col-4">
                                                    <h5 className="text-success mb-0">{processingCount}/{totalToProcess}</h5>
                                                    <small className="text-muted">Progress</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="progress mt-3" style={{ height: '20px' }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                            role="progressbar"
                                            style={{ width: `${progressPercent}%` }}
                                        >
                                            {progressPercent}%
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Single-module: Completion Card */}
                        {isSingleMode && showComplete && (
                            <div className="card bg-success bg-opacity-10 border-success mb-4">
                                <div className="card-body text-center py-4">
                                    <i className="fa fa-check-circle text-success" style={{ fontSize: '48px' }}></i>
                                    <h4 className="mt-3 text-success">Processing Complete!</h4>
                                    <p className="mb-0">Processed {processingCount} results in {formatDuration(elapsedTime)}</p>
                                </div>
                            </div>
                        )}

                        {/* Single-module: Stats Cards */}
                        {isSingleMode && results.length > 0 && !isProcessing && (
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <div className="card bg-light h-100">
                                        <div className="card-body text-center py-3">
                                            <h2 className="text-primary mb-0">{results.length}</h2>
                                            <small className="text-muted">Total Students</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-success bg-opacity-10 h-100">
                                        <div className="card-body text-center py-3">
                                            <h2 className="text-success mb-0">{processedCount}</h2>
                                            <small className="text-muted">Processed</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-warning bg-opacity-10 h-100">
                                        <div className="card-body text-center py-3">
                                            <h2 className="text-warning mb-0">{pendingCount}</h2>
                                            <small className="text-muted">Pending</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Empty state info */}
                        {selectedModules.length === 0 && !isProcessing && (
                            <div className="card bg-light mb-4">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3"><i className="fa fa-info-circle me-2 text-primary"></i>What Happens When You Process Results?</h6>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="d-flex align-items-start mb-2">
                                                <span className="badge bg-primary me-2">1</span>
                                                <div><strong>Load Students</strong><br /><small className="text-muted">Fetch all students with exam scores for the selected module(s)</small></div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="d-flex align-items-start mb-2">
                                                <span className="badge bg-primary me-2">2</span>
                                                <div><strong>Calculate Grades</strong><br /><small className="text-muted">Apply grade settings to calculate grades based on total score</small></div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="d-flex align-items-start mb-2">
                                                <span className="badge bg-primary me-2">3</span>
                                                <div><strong>Update Records</strong><br /><small className="text-muted">Save grades and decisions to student result entries</small></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Single-module: Results Table */}
                        {isSingleMode && tableData.length > 0 && (
                            <AgReportTable columns={columns} data={tableData} height={'500px'} />
                        )}

                        {isSingleMode && results.length === 0 && !isProcessing && (
                            <div className="alert alert-info text-center">
                                <i className="fa fa-info-circle me-2"></i>
                                No results found for this module
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Module Picker Modal */}
            {showModuleModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="fa fa-th-list me-2 text-primary"></i>
                                    Select Modules to Process
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowModuleModal(false)}></button>
                            </div>
                            <div className="modal-body p-0">
                                {/* Selection toolbar */}
                                <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-light">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-primary"
                                        onClick={() => setCheckedModuleCodes(new Set(moduleListSelect.map(m => m.value)))}
                                    >
                                        <i className="fa fa-check-square me-1"></i>Select All ({moduleListSelect.length})
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={() => setCheckedModuleCodes(new Set())}
                                        disabled={checkedModuleCodes.size === 0}
                                    >
                                        <i className="fa fa-square me-1"></i>Deselect All
                                    </button>
                                    <span className="ms-auto text-muted small fw-semibold">
                                        {checkedModuleCodes.size} of {moduleListSelect.length} selected
                                    </span>
                                </div>
                                {/* DataTable — no pagination, scrollable via modal-dialog-scrollable */}
                                <DataTable
                                    paging={false}
                                    data={{
                                        columns: [
                                            {
                                                label: (
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                        checked={checkedModuleCodes.size === moduleListSelect.length && moduleListSelect.length > 0}
                                                        ref={el => { if (el) el.indeterminate = checkedModuleCodes.size > 0 && checkedModuleCodes.size < moduleListSelect.length; }}
                                                        onChange={() => setCheckedModuleCodes(
                                                            checkedModuleCodes.size === moduleListSelect.length
                                                                ? new Set()
                                                                : new Set(moduleListSelect.map(m => m.value))
                                                        )}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                ),
                                                field: 'checkbox'
                                            },
                                            { label: 'S/N', field: 'sn' },
                                            { label: 'Module Code', field: 'code' },
                                            { label: 'Module Name', field: 'name' },
                                        ],
                                        rows: moduleListSelect.map((m, i) => ({
                                            sn: i + 1,
                                            code: m.value,
                                            name: m.name || m.label,
                                            checkbox: (
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={checkedModuleCodes.has(m.value)}
                                                    onChange={() => toggleModule(m.value)}
                                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                />
                                            ),
                                        }))
                                    }}
                                />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowModuleModal(false)}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={confirmModuleSelection}
                                    disabled={checkedModuleCodes.size === 0}
                                >
                                    <i className="fa fa-check me-2"></i>
                                    Confirm Selection ({checkedModuleCodes.size})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Batch Summary Modal */}
            {showBatchModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header bg-success text-white">
                                <h5 className="modal-title">
                                    <i className="fa fa-check-circle me-2"></i>
                                    Batch Processing Complete!
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={resetProcess}></button>
                            </div>
                            <div className="modal-body p-0">
                                <div className="row g-0 text-center border-bottom">
                                    <div className="col-4 p-3" style={{ backgroundColor: '#e8f5e9' }}>
                                        <h3 className="text-success mb-0">{selectedModules.length}</h3>
                                        <small className="text-success">Modules</small>
                                    </div>
                                    <div className="col-4 p-3" style={{ backgroundColor: '#e3f2fd' }}>
                                        <h3 className="text-primary mb-0">{batchTotalProcessed}</h3>
                                        <small className="text-primary">Results Processed</small>
                                    </div>
                                    <div className="col-4 p-3" style={{ backgroundColor: '#ffebee' }}>
                                        <h3 className="text-danger mb-0">{batchTotalErrors}</h3>
                                        <small className="text-danger">Errors</small>
                                    </div>
                                </div>
                                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                    <table className="table table-hover table-sm mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th style={{ width: '50px' }}>S/N</th>
                                                <th style={{ width: '120px' }}>Module Code</th>
                                                <th>Module Name</th>
                                                <th className="text-center" style={{ width: '100px' }}>Processed</th>
                                                <th className="text-center" style={{ width: '80px' }}>Errors</th>
                                                <th className="text-center" style={{ width: '70px' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {batchResults.map((r, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td><span className="badge bg-secondary">{r.moduleCode}</span></td>
                                                    <td>{r.moduleName}</td>
                                                    <td className="text-center"><span className="badge bg-primary">{r.processed}</span></td>
                                                    <td className="text-center"><span className={`badge ${r.errors > 0 ? 'bg-danger' : 'bg-light text-muted'}`}>{r.errors}</span></td>
                                                    <td className="text-center">
                                                        {r.status === 'success' && <i className="fa fa-check-circle text-success" style={{ fontSize: '18px' }}></i>}
                                                        {r.status === 'partial' && <i className="fa fa-exclamation-circle text-warning" style={{ fontSize: '18px' }}></i>}
                                                        {r.status === 'failed' && <i className="fa fa-times-circle text-danger" style={{ fontSize: '18px' }}></i>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer bg-light">
                                <button type="button" className="btn btn-primary px-4" onClick={resetProcess}>
                                    <i className="fa fa-check me-2"></i>Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({ loginData: state.LoginDetails[0] });
export default connect(mapStateToProps, null)(ProcessResult);
